import { useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, kycApi, relTime, type KycInfo, type KycStatus } from '../lib/api'
import { useToast } from '../components/Toast'

const STATUS: Record<KycStatus, { text: string; cls: string }> = {
  not_submitted: { text: 'Not submitted', cls: 'neutral' },
  submitted: { text: 'Under review', cls: 'pending' },
  more_info: { text: 'More information needed', cls: 'pending' },
  approved: { text: 'Verified', cls: 'ok' },
  rejected: { text: 'Not approved', cls: 'failed' },
}

export function KycBadge({ s }: { s: KycStatus }) {
  const b = STATUS[s] ?? { text: s, cls: 'info' }
  return <span className={`badge ${b.cls}`}>{b.text}</span>
}

const fmtSize = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

// ==================================================================== customer

export function Verification() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading, error } = useQuery({ queryKey: ['kyc'], queryFn: kycApi.get })
  const [busyKind, setBusyKind] = useState<string | null>(null)
  const [extraKind, setExtraKind] = useState('other')

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['kyc'] })
    qc.invalidateQueries({ queryKey: ['me'] })
  }

  async function upload(kind: string, file?: File | null) {
    if (!file) return
    setBusyKind(kind)
    try {
      await kycApi.upload(kind, file)
      toast.success(`${file.name} uploaded.`)
      refresh()
    } catch (e) {
      toast.error((e as ApiError).message || 'Upload failed.')
    } finally {
      setBusyKind(null)
    }
  }

  const remove = useMutation({
    mutationFn: (id: string) => kycApi.remove(id),
    onSuccess: refresh,
    onError: (e) => toast.error((e as ApiError).message),
  })
  const submit = useMutation({
    mutationFn: kycApi.submit,
    onSuccess: () => { toast.success('Submitted. We will email you when the review is done.'); refresh() },
    onError: (e) => toast.error((e as ApiError).message),
  })

  if (isLoading) return <div className="skeleton" style={{ height: 320 }} />
  if (error || !data) return <div className="alert error">{(error as Error)?.message ?? 'Could not load verification.'}</div>

  const editable = data.status === 'not_submitted' || data.status === 'more_info' || data.status === 'rejected'
  const allRequired = data.required.every(r => r.uploaded)
  const company = data.account_type === 'company'

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h1>Verify your identity</h1>
        <KycBadge s={data.status} />
      </div>
      <p className="lede">
        Every account is checked by our compliance team before it can send. This keeps the sending
        reputation you share with other customers clean. {company ? 'Upload your company documents' : 'Upload your ID'} below
        and submit them for review — it usually takes 24–48 hours. You can set up your domains in the meantime.
      </p>

      {data.status === 'submitted' && (
        <div className="alert info">Your documents are with our compliance team (submitted {data.submitted_at ? relTime(data.submitted_at) : ''}). We will email you as soon as there is a decision.</div>
      )}
      {data.status === 'approved' && (
        <div className="alert ok">Your account is verified. <Link to="/">Go to your dashboard →</Link></div>
      )}
      {(data.status === 'rejected' || data.status === 'more_info') && data.review_note && (
        <div className="alert error">
          <div><strong>{data.status === 'rejected' ? 'Why we could not approve the account' : 'What we need from you'}</strong></div>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{data.review_note}</div>
          <div className="small" style={{ marginTop: 6 }}>Upload the corrected documents below and submit again.</div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ marginTop: 0 }}>{company ? 'Company documents' : 'Your documents'}</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          JPG, PNG, WEBP, HEIC or PDF, up to 10 MB each. Make sure every corner is visible and the text is readable.
        </p>
        <table className="data">
          <tbody>
            {data.required.map(r => {
              const files = data.documents.filter(d => d.kind === r.kind)
              return (
                <tr key={r.kind}>
                  <td style={{ width: '45%' }}>
                    <strong>{r.label}</strong>
                    <span className="small muted" style={{ display: 'block' }}>Required</span>
                  </td>
                  <td>
                    {files.length === 0 && <span className="badge neutral">Missing</span>}
                    {files.map(f => (
                      <div key={f.id} className="row small" style={{ gap: 8 }}>
                        <span className="badge ok">Uploaded</span>
                        <span className="truncate" title={f.file_name}>{f.file_name}</span>
                        <span className="muted">{fmtSize(f.size_bytes)}</span>
                        {editable && <button className="linklike" onClick={() => remove.mutate(f.id)}>Remove</button>}
                      </div>
                    ))}
                  </td>
                  <td className="right">
                    {editable && <FilePick label={files.length ? 'Replace / add' : 'Upload'} busy={busyKind === r.kind}
                                           onPick={f => upload(r.kind, f)} />}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {data.documents.filter(d => !data.required.some(r => r.kind === d.kind)).length > 0 && (
          <>
            <h3 style={{ marginBottom: 6 }}>Other files</h3>
            {data.documents.filter(d => !data.required.some(r => r.kind === d.kind)).map(f => (
              <div key={f.id} className="row small" style={{ gap: 8, marginBottom: 4 }}>
                <span className="badge info">{f.label}</span>
                <span className="truncate">{f.file_name}</span>
                <span className="muted">{fmtSize(f.size_bytes)}</span>
                {editable && <button className="linklike" onClick={() => remove.mutate(f.id)}>Remove</button>}
              </div>
            ))}
          </>
        )}

        {editable && (
          <div className="row" style={{ marginTop: 16, gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0, minWidth: 240 }}>
              <label htmlFor="extra-kind">Add a supporting document (optional)</label>
              <select id="extra-kind" value={extraKind} onChange={e => setExtraKind(e.target.value)}>
                {data.kinds.filter(k => !data.required.some(r => r.kind === k.kind)).map(k => (
                  <option key={k.kind} value={k.kind}>{k.label}</option>
                ))}
              </select>
            </div>
            <FilePick label="Choose file" busy={busyKind === extraKind} secondary onPick={f => upload(extraKind, f)} />
          </div>
        )}
      </div>

      {editable && (
        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => submit.mutate()} disabled={!allRequired || submit.isPending}>
            {submit.isPending ? 'Submitting…' : 'Submit for review'}
          </button>
          {!allRequired && <span className="small muted">Upload every required document to submit.</span>}
        </div>
      )}

      <p className="small muted" style={{ marginTop: 24 }}>
        Documents are encrypted in transit (TLS) and at rest (AES-256), stored privately, and only our
        compliance team can open them. See our <a href="https://itsbingmybusiness.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
      </p>
    </div>
  )
}

function FilePick({ label, busy, onPick, secondary }: { label: string; busy: boolean; onPick: (f: File | null) => void; secondary?: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  return (
    <>
      <input ref={ref} type="file" hidden accept=".jpg,.jpeg,.png,.webp,.heic,.pdf,image/*,application/pdf"
             onChange={e => { onPick(e.target.files?.[0] ?? null); e.target.value = '' }} />
      <button type="button" className={secondary ? 'secondary small' : 'small'} disabled={busy} onClick={() => ref.current?.click()}>
        {busy ? 'Uploading…' : label}
      </button>
    </>
  )
}

/** The customer's daily allowance and how it is split between transactional and marketing mail. */
export function SendingLimits() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data } = useQuery({ queryKey: ['limits'], queryFn: kycApi.limits })
  const [pct, setPct] = useState<number | null>(null)
  const save = useMutation({
    mutationFn: (p: number) => kycApi.setSplit(p),
    onSuccess: (d) => { qc.setQueryData(['limits'], d); setPct(null); toast.success('Split saved.') },
    onError: (e) => toast.error((e as ApiError).message),
  })
  if (!data) return <div className="skeleton" style={{ height: 220 }} />
  const value = pct ?? data.transactional_percent
  const tx = Math.floor(data.daily_limit * value / 100)
  return (
    <div>
      <h1>Sending limits</h1>
      <p className="lede">
        Your account can send <strong>{data.daily_limit.toLocaleString()}</strong> emails per day in total. Decide how much of
        that goes to transactional mail (API and SMTP: receipts, sign-in codes, notifications) and how much to marketing campaigns.
        Need more? Ask us from <Link to="/billing">Billing</Link>.
      </p>
      <div className="card" style={{ maxWidth: 620 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div><div className="small muted">Transactional</div><div style={{ fontSize: 26, fontWeight: 700 }}>{tx.toLocaleString()}<span className="small muted"> /day</span></div></div>
          <div style={{ textAlign: 'right' }}><div className="small muted">Marketing</div><div style={{ fontSize: 26, fontWeight: 700 }}>{(data.daily_limit - tx).toLocaleString()}<span className="small muted"> /day</span></div></div>
        </div>
        <input type="range" min={0} max={100} step={5} value={value} aria-label="Transactional share"
               onChange={e => setPct(Number(e.target.value))} style={{ width: '100%', margin: '18px 0 6px' }} />
        <div className="row small muted" style={{ justifyContent: 'space-between' }}>
          <span>All marketing</span><span>{value}% transactional</span><span>All transactional</span>
        </div>
        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          <button disabled={pct === null || save.isPending} onClick={() => pct !== null && save.mutate(pct)}>
            {save.isPending ? 'Saving…' : 'Save split'}
          </button>
          {pct !== null && <button className="ghost" onClick={() => setPct(null)}>Reset</button>}
        </div>
      </div>
    </div>
  )
}

// ==================================================================== operator

const FILTERS: { v: string; t: string }[] = [
  { v: 'submitted', t: 'To review' },
  { v: 'more_info', t: 'Waiting on customer' },
  { v: 'not_submitted', t: 'Not submitted' },
  { v: 'approved', t: 'Approved' },
  { v: 'rejected', t: 'Rejected' },
  { v: '', t: 'All' },
]

export function AdminVerifications() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? 'submitted'
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'kyc', status], queryFn: () => kycApi.admin.list(status || undefined) })
  const rows = data?.data ?? []
  return (
    <div>
      <h1>Verifications</h1>
      <p className="lede">Identity documents from new accounts. Approve to open sending, reject with a reason, or ask for more — the customer is emailed every time.</p>
      <div className="toolbar">
        <div className="segmented" role="tablist">
          {FILTERS.map(f => (
            <button key={f.v || 'all'} className={status === f.v ? 'active' : ''} onClick={() => setParams(f.v ? { status: f.v } : { status: '' })}>{f.t}</button>
          ))}
        </div>
      </div>
      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && rows.length === 0 && <div className="empty"><p><strong>Nothing here</strong></p><p>No accounts in this state.</p></div>}
      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>Account</th><th>Type</th><th>Country</th><th>Documents</th><th>Status</th><th>Submitted</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.tenant_id}>
                  <td><Link to={`/admin/verifications/${r.tenant_id}`}><strong>{r.name}</strong></Link>
                    <span className="small muted" style={{ display: 'block' }}>{r.owner_email}</span></td>
                  <td className="small">{r.account_type}</td>
                  <td className="small">{r.country ?? '—'}</td>
                  <td className="small">{r.documents}</td>
                  <td><KycBadge s={r.kyc_status} /></td>
                  <td className="small muted">{r.submitted_at ? relTime(r.submitted_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function AdminVerificationDetail() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading, error } = useQuery({ queryKey: ['admin', 'kyc', 'detail', id], queryFn: () => kycApi.admin.detail(id) })
  const [reason, setReason] = useState('')
  const [limit, setLimit] = useState<string>('')
  const [split, setSplit] = useState<string>('')

  const done = (msg: string) => () => {
    toast.success(msg)
    setReason('')
    qc.invalidateQueries({ queryKey: ['admin', 'kyc'] })
  }
  const fail = (e: unknown) => toast.error((e as ApiError).message)
  const lim = () => ({
    daily_limit: limit ? Number(limit) : undefined,
    transactional_percent: split ? Number(split) : undefined,
  })
  const approve = useMutation({ mutationFn: () => kycApi.admin.approve(id, { reason: reason || undefined, ...lim() }), onSuccess: done('Approved — the customer has been emailed.'), onError: fail })
  const reject = useMutation({ mutationFn: () => kycApi.admin.reject(id, reason), onSuccess: done('Rejected — the customer has been emailed the reason.'), onError: fail })
  const more = useMutation({ mutationFn: () => kycApi.admin.requestInfo(id, reason), onSuccess: done('Request sent to the customer.'), onError: fail })
  const setLimits = useMutation({ mutationFn: () => kycApi.admin.setLimits(id, lim()), onSuccess: done('Limits updated.'), onError: fail })

  async function view(docId: string) {
    try {
      const { url } = await kycApi.admin.documentUrl(docId)
      window.open(url, '_blank', 'noopener')
    } catch (e) { fail(e) }
  }

  if (isLoading) return <div className="skeleton" style={{ height: 320 }} />
  if (error || !data) return <div className="alert error">{(error as Error)?.message ?? 'Not found.'}</div>
  const v: KycInfo = data.verification
  const t = data.tenant
  const busy = approve.isPending || reject.isPending || more.isPending

  return (
    <div>
      <p className="small"><Link to="/admin/verifications">← Verifications</Link></p>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h1>{t.name}</h1>
        <KycBadge s={v.status} />
      </div>

      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Applicant</h2>
          <table className="kv"><tbody>
            <tr><td>Account type</td><td>{v.account_type}</td></tr>
            {v.contact_name && <tr><td>Contact</td><td>{v.contact_name}</td></tr>}
            <tr><td>Owner email</td><td>{t.owner_email ?? '—'}</td></tr>
            <tr><td>Phone</td><td className="mono">{v.phone ?? '—'}</td></tr>
            <tr><td>Country</td><td>{v.country ?? '—'}</td></tr>
            <tr><td>Website</td><td>{v.website ? <a href={v.website} target="_blank" rel="noreferrer">{v.website}</a> : '—'}</td></tr>
            <tr><td>Use case</td><td>{t.use_case ?? '—'}</td></tr>
            <tr><td>Signup IP</td><td className="mono">{t.signup_ip ?? '—'}</td></tr>
            <tr><td>Account status</td><td>{t.status} · plan {t.plan ?? '—'}</td></tr>
            <tr><td>Created</td><td>{new Date(t.created_at).toLocaleString()}</td></tr>
          </tbody></table>
          <p className="small"><Link to={`/admin/tenants/${t.id}`}>Open tenant →</Link></p>
        </div>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Documents</h2>
          {v.documents.length === 0 && <p className="small muted">No documents uploaded.</p>}
          {v.documents.map(d => (
            <div key={d.id} className="row small" style={{ justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <div><strong>{d.label}</strong><span className="muted" style={{ display: 'block' }}>{d.file_name} · {fmtSize(d.size_bytes)} · {relTime(d.uploaded_at)}</span></div>
              <button className="secondary small" onClick={() => view(d.id)}>View</button>
            </div>
          ))}
          <p className="small muted">Links open for 5 minutes and every view is recorded in the audit log.</p>
          {v.review_note && <div className="alert info small"><strong>Last note to customer:</strong> {v.review_note}</div>}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Sending limits</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Now {v.limits.daily_limit}/day · {v.limits.transactional_daily} transactional · {v.limits.marketing_daily} marketing. Leave empty to keep the current value
          (defaults on approval: individual 100, company 250).
        </p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}><label htmlFor="dl">Emails per day</label>
            <input id="dl" type="number" min={0} value={limit} placeholder={String(v.limits.daily_limit)} onChange={e => setLimit(e.target.value)} /></div>
          <div className="field" style={{ marginBottom: 0 }}><label htmlFor="sp">Transactional %</label>
            <input id="sp" type="number" min={0} max={100} value={split} placeholder={String(v.limits.transactional_percent)} onChange={e => setSplit(e.target.value)} /></div>
          <button className="secondary" disabled={setLimits.isPending || (!limit && !split)} onClick={() => setLimits.mutate()}>Save limits</button>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Decision</h2>
        <div className="field">
          <label htmlFor="reason">Message to the customer</label>
          <textarea id="reason" rows={4} value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Required for reject or request-more. Optional note for approval. The customer sees this text in the email." />
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button disabled={busy} onClick={() => approve.mutate()}>{v.documents.length ? 'Approve' : 'Approve without documents'}</button>
          <button className="secondary" disabled={busy || reason.trim().length < 5} onClick={() => more.mutate()}>Ask for more information</button>
          <button className="danger" disabled={busy || reason.trim().length < 5} onClick={() => reject.mutate()}>Reject</button>
        </div>
      </div>
    </div>
  )
}
